import type { Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import type { ReceiptRow } from '../db/types';
import { articleName } from './prices';
import { buildMergeMap, resolveWith } from './products';
import { linePaidGr } from './receipts';

// Wydatki z paragonów (Lidl): sumy miesięcy i na co poszły pieniądze w wybranym miesiącu.

export interface MonthSpend {
  /** „2026-09”. */
  key: string;
  totalGr: number;
  savingsGr: number;
  receipts: number;
}

export const monthKey = (ms: number) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export function monthLabel(key: string, style: 'long' | 'short' = 'long'): string {
  const [y, m] = key.split('-').map(Number);
  const s = new Date(y!, m! - 1, 1).toLocaleDateString('pl-PL', style === 'long' ? { month: 'long', year: 'numeric' } : { month: 'short' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Ostatnie `count` miesięcy kalendarzowych (także bez zakupów), od najstarszego. */
export function monthsBack(count: number, now = new Date()): string[] {
  const out: string[] = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(monthKey(d.getTime()));
  }
  return out;
}

export function monthlySpend(receipts: readonly ReceiptRow[], keys: readonly string[]): MonthSpend[] {
  const by = new Map(keys.map((k) => [k, { key: k, totalGr: 0, savingsGr: 0, receipts: 0 }]));
  for (const r of receipts) {
    if (r.deleted || !r.purchasedMs) continue;
    const m = by.get(monthKey(r.purchasedMs));
    if (!m) continue;
    m.totalGr += r.totalGr ?? 0;
    m.savingsGr += r.savingsGr ?? 0;
    m.receipts++;
  }
  return keys.map((k) => by.get(k)!);
}

export interface SpendItem {
  /** Produkt Odhaczki albo artykuł bez produktu. */
  kind: 'product' | 'article';
  id: Id;
  name: string;
  totalGr: number;
  count: number;
}

/** Na co poszły pieniądze w miesiącu: linie paragonów zebrane po produkcie (albo artykule bez produktu). */
export function useMonthItems(key: string | null): SpendItem[] | undefined {
  return useLiveQuery(async () => {
    if (!key) return [];
    const receipts = (await db.receipts.toArray()).filter((r) => !r.deleted && r.purchasedMs && monthKey(r.purchasedMs) === key);
    if (receipts.length === 0) return [];
    const lines = (await db.receiptLines.where('receiptId').anyOf(receipts.map((r) => r.id)).toArray()).filter((l) => !l.deleted);
    const [articles, products] = await Promise.all([db.articles.bulkGet([...new Set(lines.map((l) => l.articleId!).filter(Boolean))]), db.products.toArray()]);
    const resolve = resolveWith(buildMergeMap(products));
    const productName = new Map(products.map((p) => [p.id, p.name]));
    const art = new Map(articles.filter((a) => !!a).map((a) => [a!.id, a!]));
    const out = new Map<string, SpendItem>();
    for (const l of lines) {
      const a = l.articleId ? art.get(l.articleId) : undefined;
      const pid = a?.productId ? resolve(a.productId) : null;
      const k = pid ? `p:${pid}` : `a:${l.articleId ?? l.id}`;
      const cur = out.get(k) ?? {
        kind: pid ? ('product' as const) : ('article' as const),
        id: pid ?? l.articleId ?? l.id,
        name: pid ? (productName.get(pid) ?? '?') : a ? articleName(a) : (l.rawName ?? '?'),
        totalGr: 0,
        count: 0,
      };
      cur.totalGr += linePaidGr(l);
      cur.count++;
      out.set(k, cur);
    }
    return [...out.values()].sort((a, b) => b.totalGr - a.totalGr);
  }, [key]);
}
